import React from "react";
import {
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
} from "date-fns";
import { CalendarEvent } from "../../utils/calendarUtils";
import { getEventsForDay, EVENT_COLORS } from "../../utils/calendarUtils";

interface YearViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  onMonthClick: (date: Date) => void;
}

const MONTHS = Array.from({ length: 12 }, (_, i) => i);

const MiniMonth: React.FC<{
  year: number;
  month: number;
  events: CalendarEvent[];
  onClick: () => void;
}> = ({ year, month, events, onClick }) => {
  const monthStart = startOfMonth(new Date(year, month, 1));
  const monthEnd = endOfMonth(monthStart);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const allDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  const today = new Date();

  return (
    <button
      onClick={onClick}
      className="rounded-lg border border-gray-200 p-2 text-left transition-shadow hover:shadow-md"
    >
      <div className="mb-1 text-center text-sm font-semibold text-gray-900">
        {format(monthStart, "MMMM")}
      </div>
      <div className="grid grid-cols-7 gap-px">
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
          <div key={i} className="text-center text-[9px] text-gray-400">
            {d}
          </div>
        ))}
        {allDays.map((day, i) => {
          const inMonth = isSameMonth(day, monthStart);
          const isToday = isSameDay(day, today);
          const dayEvents = getEventsForDay(events, day);
          const uniqueTypes = [...new Set(dayEvents.map((e) => e.type))];

          return (
            <div
              key={i}
              className="relative flex h-5 items-center justify-center"
            >
              <span
                className={`text-[10px] ${
                  isToday
                    ? "font-bold text-primary-600"
                    : inMonth
                    ? "text-gray-700"
                    : "text-gray-200"
                }`}
              >
                {format(day, "d")}
              </span>
              {dayEvents.length > 0 && inMonth && (
                <div className="absolute -bottom-0.5 flex gap-px">
                  {uniqueTypes.slice(0, 3).map((type) => (
                    <span
                      key={type}
                      className={`h-1 w-1 rounded-full ${EVENT_COLORS[type].dot}`}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </button>
  );
};

const YearView: React.FC<YearViewProps> = ({
  currentDate,
  events,
  onMonthClick,
}) => {
  const year = currentDate.getFullYear();

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
      {MONTHS.map((month) => (
        <MiniMonth
          key={month}
          year={year}
          month={month}
          events={events}
          onClick={() => onMonthClick(new Date(year, month, 1))}
        />
      ))}
    </div>
  );
};

export default YearView;
